import { AUTH_URL, withAppId } from './apiConfig';

const AUTH_TOKEN_KEY = 'authToken';
const AUTH_USER_KEY = 'authUser';
const AUTH_LOGIN_SESSION_KEY = 'authLoginSession';
const CT_PROJECT_ID_KEY = 'ct_project_id';
const CT_PROJECT_NAME_KEY = 'ct_project_name';
const LOGIN_STORAGE_KEYS = [
  'access_token', 'refresh_token', 'id_token', 'token_type', 'expires_in',
  'refresh_expires_in', 'menu', 'footermenu', 'menuPermission', 'default_values',
  'roleId', 'companyName', 'preferred_Username', 'user_Email', 'userName',
  'userId', 'tenantId', 'businessId', 'businessUnitId', 'activeKey',
  'ct_project_id', 'ct_project_name',
];

let loginPromise = null;
let refreshPromise = null;
let runtimeAuthToken = null;

export function setAuthTokenOverride(token) {
  runtimeAuthToken = typeof token === 'string' && token.trim() ? token.trim() : null;
}

export function clearAuthTokenOverride() {
  runtimeAuthToken = null;
}

function extractToken(json) {
  return (
    json?.token ?? json?.access_token ?? json?.accessToken ?? json?.jwt ??
    json?.data?.token ?? json?.data?.access_token ?? json?.data?.accessToken ?? json?.data?.jwt
  );
}

function extractUser(json) {
  return json?.user ?? json?.data?.user ?? json?.data?.profile ?? null;
}

function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')));
  } catch {
    return null;
  }
}

function setLocalStorageJson(key, value) {
  if (value === undefined || value === null) return;
  localStorage.setItem(key, JSON.stringify(value));
}

function getLoginMenus(json, data) {
  return data?.menus ?? json?.menus ?? {
    menu: data?.menu ?? json?.menu,
    footermenu: data?.footermenu ?? json?.footermenu,
  };
}

function persistLoginResponse(json, token, user) {
  // Menu/permission data is login/role scoped. Never let values from the
  // previous authenticated user survive when the next login response omits a
  // field; otherwise two users with the same role can render different sidebars
  // purely because one browser still has stale localStorage.
  ['menu', 'footermenu', 'menuPermission', 'default_values', 'activeKey'].forEach((key) => {
    localStorage.removeItem(key);
  });

  const data = json?.data ?? json;
  const tokenPayload = decodeJwtPayload(token) ?? {};
  const menus = getLoginMenus(json, data);
  const userName = (
    user?.userName ?? user?.name ?? data?.userName ?? data?.name ??
    tokenPayload.userName ?? tokenPayload.name ?? tokenPayload.preferred_username ?? tokenPayload.email
  );
  const preferredUsername = (
    user?.preferred_Username ?? user?.preferred_username ??
    data?.preferred_Username ?? data?.preferred_username ??
    tokenPayload.preferred_Username ?? tokenPayload.preferred_username ??
    tokenPayload.email ?? userName
  );
  const userEmail = user?.email ?? data?.email ?? tokenPayload.email ?? preferredUsername;

  localStorage.setItem('access_token', token);
  if (data?.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
  if (data?.id_token !== undefined) localStorage.setItem('id_token', data.id_token);
  if (data?.token_type) localStorage.setItem('token_type', data.token_type);
  if (data?.expires_in !== undefined) localStorage.setItem('expires_in', String(data.expires_in));
  if (data?.refresh_expires_in !== undefined) localStorage.setItem('refresh_expires_in', String(data.refresh_expires_in));
  if (userName) localStorage.setItem('userName', userName);
  if (preferredUsername) localStorage.setItem('preferred_Username', preferredUsername);
  if (userEmail) localStorage.setItem('user_Email', userEmail);
  if (data?.roleId ?? tokenPayload.role_id) localStorage.setItem('roleId', data?.roleId ?? tokenPayload.role_id);
  if (data?.companyName) localStorage.setItem('companyName', data.companyName);
  if (tokenPayload.userId !== undefined) localStorage.setItem('userId', String(tokenPayload.userId));
  if (tokenPayload.tenantId) localStorage.setItem('tenantId', tokenPayload.tenantId);
  if (tokenPayload.businessId) localStorage.setItem('businessId', tokenPayload.businessId);
  if (tokenPayload.businessUnitId) localStorage.setItem('businessUnitId', tokenPayload.businessUnitId);

  setLocalStorageJson('menu', menus.menu);
  setLocalStorageJson('footermenu', menus.footermenu);
  setLocalStorageJson('menuPermission', data?.menuPermission);
  setLocalStorageJson('default_values', data?.default_values);
}

// ── Token & Session ──────────────────────────────────────────────────────────

export function getStoredToken() {
  if (runtimeAuthToken) return runtimeAuthToken;

  // localStorage is shared by every tab/window for the same origin.
  // Do not gate the persisted token with sessionStorage: sessionStorage is
  // isolated per tab/window, which made a newly opened window look logged out
  // even though a valid auth token already existed.
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function getStoredUser() {
  try {
    const stored = localStorage.getItem(AUTH_USER_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // Fall through to the legacy login fields below.
  }
  return {
    userId: localStorage.getItem('userId'),
    username: localStorage.getItem('userName') || localStorage.getItem('preferred_Username'),
    email: localStorage.getItem('user_Email'),
    roleId: localStorage.getItem('roleId'),
  };
}

export function logout() {
  // No response may outlive the session that was entitled to it.
  invalidateGetCache();
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
  LOGIN_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  sessionStorage.removeItem(AUTH_LOGIN_SESSION_KEY);
  window.dispatchEvent(new Event('auth:logout'));
}

// ── Role Helpers ─────────────────────────────────────────────────────────────

export function getCurrentRoleName() {
  const stored = String(localStorage.getItem('roleId') ?? '').trim().toLowerCase();
  if (stored) return stored;
  const payload = decodeJwtPayload(getStoredToken()) ?? {};
  return String(payload.role ?? payload.roleName ?? payload.role_name ?? payload.role_id ?? payload.roleId ?? '').trim().toLowerCase();
}

export function getUserRoles() {
  const token = getStoredToken() ?? localStorage.getItem(AUTH_TOKEN_KEY);
  const payload = decodeJwtPayload(token);
  const roles = new Set();
  const stored = getCurrentRoleName();
  if (stored) roles.add(stored);
  if (payload) {
    (payload?.realm_access?.roles ?? []).forEach((role) => roles.add(role));
    Object.values(payload?.resource_access ?? {}).forEach((client) => {
      (client?.roles ?? []).forEach((role) => roles.add(role));
    });
    [payload?.role, payload?.roleName, payload?.role_name].forEach((role) => {
      if (role) roles.add(role);
    });
  }
  return Array.from(roles).map((role) => String(role).toLowerCase());
}

export function isTester() {
  return getCurrentRoleName() === 'tester' || getUserRoles().includes('tester');
}

// getActingIdentity returns the numeric {roleId, userId} of the logged-in user,
// read from the auth token (falling back to the stored roleId). Used to evaluate
// admin-configured field autoValueRules for UX only — the gateway re-evaluates
// the same rules from the token and has the final say, so nothing here is a
// security boundary.
export function getActingIdentity() {
  const payload = decodeJwtPayload(getStoredToken() ?? localStorage.getItem(AUTH_TOKEN_KEY)) ?? {};
  const num = (value) => {
    if (value === undefined || value === null || value === '') return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };
  return {
    roleId: num(payload.role_id ?? payload.roleId) ?? num(localStorage.getItem('roleId')),
    userId: num(payload.userId ?? payload.user_id),
  };
}

// ── Project Context ───────────────────────────────────────────────────────────

export function setSelectedProject(project) {
  if (!project) return;
  localStorage.setItem(CT_PROJECT_ID_KEY, String(project.id));
  localStorage.setItem(CT_PROJECT_NAME_KEY, String(project.name));
}

export function getSelectedProject() {
  const id = localStorage.getItem(CT_PROJECT_ID_KEY);
  const name = localStorage.getItem(CT_PROJECT_NAME_KEY);
  if (!id || !name) return null;
  return { id: Number(id), name };
}

export function clearSelectedProject() {
  localStorage.removeItem(CT_PROJECT_ID_KEY);
  localStorage.removeItem(CT_PROJECT_NAME_KEY);
}

// ── Login ────────────────────────────────────────────────────────────────────

export async function login(credentials) {
  if (loginPromise) return loginPromise;

  loginPromise = fetch(`${AUTH_URL}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(credentials),
  })
    .then(async (res) => {
      const json = await res.json();
      if (!res.ok) {
        const error = new Error(json?.message ?? json?.error ?? `Login failed: ${res.status}`);
        error.status = res.status;
        throw error;
      }
      const token = extractToken(json);
      if (!token) throw new Error('No token in login response');
      const user = extractUser(json);
      localStorage.setItem(AUTH_TOKEN_KEY, token);
      sessionStorage.setItem(AUTH_LOGIN_SESSION_KEY, '1');
      if (user) localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
      persistLoginResponse(json, token, user);
      // Let any mounted AuthGuard re-check and reveal the protected content.
      window.dispatchEvent(new Event('auth:login'));
      return { token, user, response: json };
    })
    .finally(() => { loginPromise = null; });

  return loginPromise;
}


function tokenExpiresSoon(token, skewSeconds = 30) {
  const payload = decodeJwtPayload(token);
  const exp = Number(payload?.exp);
  if (!Number.isFinite(exp) || exp <= 0) return false;
  return (exp * 1000) <= (Date.now() + (skewSeconds * 1000));
}

function persistRefreshedAccessToken(json) {
  const token = extractToken(json);
  if (!token) return '';

  localStorage.setItem(AUTH_TOKEN_KEY, token);
  localStorage.setItem('access_token', token);

  const data = json?.data ?? json;
  const expiresIn = data?.expires_in ?? data?.accessTokenExpiresIn;
  if (expiresIn !== undefined) localStorage.setItem('expires_in', String(expiresIn));

  window.dispatchEvent(new Event('auth:token-refreshed'));
  return token;
}

export async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  const refreshToken = String(localStorage.getItem('refresh_token') ?? '').trim();
  if (!refreshToken) {
    const error = new Error('Session expired');
    error.status = 401;
    throw error;
  }

  refreshPromise = fetch(`${AUTH_URL}/generate-refresh-token`, {
    method: 'POST',
    headers: withAppId({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ refresh_token: refreshToken }),
  })
    .then(async (res) => {
      let json = {};
      try {
        json = await res.json();
      } catch {
        json = {};
      }

      if (!res.ok) {
        const error = new Error(json?.error ?? json?.message ?? `Token refresh failed: ${res.status}`);
        error.status = res.status;
        throw error;
      }

      const token = persistRefreshedAccessToken(json);
      if (!token) {
        const error = new Error('Token refresh response did not contain an access token');
        error.status = 401;
        throw error;
      }
      return token;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

// ── HTTP Helpers (exported for use by other service files) ───────────────────

export async function ensureToken() {
  const token = getStoredToken();
  if (token && !tokenExpiresSoon(token)) return token;

  if (localStorage.getItem('refresh_token')) {
    try {
      return await refreshAccessToken();
    } catch {
      // The request helper will handle a genuine refresh failure.
    }
  }

  if (token) return token;
  const error = new Error('Authentication required');
  error.status = 401;
  throw error;
}

async function authHeaders() {
  const token = await ensureToken();
  // Normal application API calls must always target the currently selected
  // project database. Do not let the global fetch interceptor infer this from
  // the URL because AUTH_URL and ADMIN_API_URL may point to the same service.
  // Explicit X-App-Id wins in the backend middleware and keeps runtime module
  // data/master dropdowns scoped to VITE_APP_ID.
  return withAppId({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  });
}

// ── GET in-flight coalescing ────────────────────────────────────────────────
//
// A page routinely asks for the SAME data several times over, because the parts
// that need it are independent and none of them can know the others exist. Six
// form fields that look up the same collection issue six identical requests;
// the header and the body of a detail page each fetch the same record. Measured
// on an ORDINARY edit form: 13 requests for 8 distinct URLs, one of them fired
// six times.
//
// The honest fix is not to make each caller aware of the others — that couples
// components that have no business knowing about each other, and breaks the
// moment a new one is added. It is to make the REQUEST idempotent: a GET that
// is already in the air is JOINED rather than reissued.
//
// Deliberately in-flight ONLY, with no time-to-live:
//   • It cannot serve stale data. The answer a joiner receives is the answer to
//     a request that had not yet returned when it asked — by definition no
//     staler than issuing its own.
//   • It needs no invalidation, so a write can never be shadowed by a cache
//     entry, and no caller has to remember to clear anything.
// A TTL would catch a few more (a child mounting just after its parent's fetch
// resolved) at the cost of a staleness window on every endpoint in the app.
// `formGroupsCache` already makes that trade deliberately for one endpoint it
// understands; this layer, which knows nothing about what it is fetching,
// should not make it for everything.
//
// Each joiner gets its OWN deep copy of the payload. Callers legitimately mutate
// what they receive (dropdownApi attaches `recordMeta` to the groups array), and
// handing several callers one shared object would turn a saved request into a
// cross-component data bug — far worse than the duplicate it removed.
//
// A SHORT TIME-TO-LIVE sits on top of the coalescing, because not every
// duplicate overlaps: a child that mounts just after its parent's fetch
// resolved asks a moment too late to join it. That is the remaining half of the
// "same data, fetched again" problem, and it is the half a pure in-flight join
// cannot reach.
//
// This part CAN serve stale data, so it is bounded hard:
//   • a few seconds only — long enough to cover one page's mount burst, short
//     enough that no realistic user action fits inside it;
//   • ANY non-GET clears the whole GET cache. A write is the one event that
//     makes previously-fetched data wrong, so nothing a user saves can be
//     shadowed by a stale entry;
//   • logging out clears it too, so no response outlives the session that was
//     entitled to it.
// This mirrors the trade `formGroupsCache` already makes deliberately for one
// endpoint; the difference is that the invalidation here is automatic rather
// than something each caller has to remember.
const GET_TTL_MS = 4000;

const inFlightGets = new Map(); // url -> Promise<json>
const settledGets = new Map();  // url -> { at, json }

// Cleared on every write and on logout — see the note above.
export function invalidateGetCache() {
  settledGets.clear();
}

function clonePayload(json) {
  // structuredClone is present in every browser this app supports and in Node
  // 17+. Falling back to a JSON round-trip keeps older test environments
  // working; both produce a copy no other caller can observe.
  try {
    return typeof structuredClone === 'function' ? structuredClone(json) : JSON.parse(JSON.stringify(json));
  } catch {
    // Non-cloneable payload (rare). Sharing the original is still correct for
    // every caller that does not mutate, and is better than failing the call.
    return json;
  }
}

function isGet(options) {
  const method = (options?.method ?? 'GET').toUpperCase();
  return method === 'GET';
}

export async function fetchJsonWithAuth(baseUrl, path = '', options = {}) {
  if (path && typeof path === 'object') {
    options = path;
    path = '';
  }

  const url = `${baseUrl}${path || ''}`;
  if (isGet(options)) {
    const pending = inFlightGets.get(url);
    if (pending) return clonePayload(await pending);
    const settled = settledGets.get(url);
    if (settled && Date.now() - settled.at < GET_TTL_MS) return clonePayload(settled.json);
    // Expired: drop it now rather than leaving the map to grow for the rest of
    // the session on a long-lived page.
    if (settled) settledGets.delete(url);
  }

  if (!isGet(options)) {
    // A write invalidates BEFORE it runs and again after it settles: before, so
    // a GET fired concurrently with the save cannot repopulate the cache from
    // pre-write state; after, so anything read during the write is dropped too.
    invalidateGetCache();
    try {
      return await performFetch(url, options);
    } finally {
      invalidateGetCache();
    }
  }

  const run = performFetch(url, options);
  inFlightGets.set(url, run);
  try {
    // The ORIGINATING caller is cloned too, so no caller can tell whether it
    // issued the request or joined one — otherwise a mutation would be safe or
    // unsafe depending on render order, which is the worst kind of bug.
    const json = await run;
    settledGets.set(url, { at: Date.now(), json });
    return clonePayload(json);
  } finally {
    // Cleared on success AND failure: a failed GET must not be inherited by
    // anyone who asks next, they retry for real.
    inFlightGets.delete(url);
  }
}

async function performFetch(url, options = {}) {
  const request = async (token) => fetch(url, {
    ...options,
    headers: {
      ...withAppId({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      }),
      ...options.headers,
    },
  });

  let token = await ensureToken();
  let res = await request(token);

  // Do not destroy the session on the first 401. A role-permission change or
  // an expired access token can make the current token stale while the stored
  // refresh token is still valid. Refresh and retry the exact request once.
  if (res.status === 401 && localStorage.getItem('refresh_token')) {
    try {
      token = await refreshAccessToken();
      res = await request(token);
    } catch {
      // Fall through; a final 401 below means the session really is invalid.
    }
  }

  if (res.status === 401) logout();

  const text = await res.text();
  let json = {};
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = { error: text };
    }
  }
  if (!res.ok) {
    const responseError = json?.error ?? json?.message;
    const detail = typeof responseError === 'string'
      ? responseError
      : responseError && Object.keys(responseError).length > 0
        ? JSON.stringify(responseError)
        : `API ${res.status}: ${res.statusText}`;
    const error = new Error(detail);
    error.status = res.status;
    throw error;
  }
  return json;
}

export async function apiGetWithAuth(baseUrl, path) {
  const json = await fetchJsonWithAuth(baseUrl, path);
  return json.data ?? json;
}
